import { UrlUtility } from '../../../util/aactools/urlUtility';
import { applicationSettings } from '../../../settings/application';
import { getCookieConsentService } from '../../ServiceHandler';
import { InsightsApi, IpexMomentEnum } from '@insights/insights-data-provider';
import { AnalyticsManager } from '@inter-ikea-kompis/analytics';
import { getLocalStorageAbVariation } from '../../../util/abTestUtility';

/**
 *   This class is used to report analytics to the Insikt analytics component
 */
export default class InsightsReporter {
  constructor() {
    this.urlUtility = new UrlUtility();
    this.hasHadFirstInteraction = false;
    this.insightsApi = new InsightsApi();
  }
  async hasConsent() {
    try {
      const consentLevel = 2;
      if (applicationSettings.uiPlatform === 'kiosk') {
        return true;
      }
      const hasConsent = await getCookieConsentService().hasCookieConsent(
        consentLevel
      );
      return hasConsent;
    } catch (error) {
      console.warn('Cant fetch consent, assume no consent');
      return false;
    }
  }

  async connect() {
    if (this._isConnected || !(await this.hasConsent())) {
      return;
    }

    const appInfo = this.urlUtility.getAppInfo();
    const versionMatch = document.location.href.match(
      /\/(\d+\.\d+\.\d+\.\d+)\//
    );
    const plannerVersion = versionMatch ? versionMatch[1] : '0.1';
    const applicationName = applicationSettings.applicationName;
    const SPR_ORDER = 'SPR_ORDER';
    const abVersionName = SPR_ORDER.concat('_', applicationName);
    const abVersionVariation = getLocalStorageAbVariation(abVersionName);

    await this.insightsApi.connectApplication({
      applicationId: applicationName.toLowerCase(),
      applicationVersion: plannerVersion,
      platform: 'owfe',
      countryCode: appInfo.country.toLowerCase(),
      languageCode: appInfo.language.toLowerCase(),
      storeId: appInfo.storeId,
      kiosk: applicationSettings.uiPlatform === 'kiosk',
      dev: !applicationSettings.isProd,
      isPlanner: true,
      enableGa: true,
      abVersionName: abVersionVariation ? abVersionName : undefined,
      abVersionVariation: abVersionVariation,
    });

    this._isConnected = true;
    AnalyticsManager.setInsightsApi(this.insightsApi);
  }

  /**
   * Sends event data to IPEX Insights.
   *
   * @param {string} ipexMoment IPEX moment string.
   * @param {string} eventString Event string.
   * @param {object} payload Optional payload.
   */
  async reportCustomEvent(event) {
    if (!(await this.canTransmit())) {
      return;
    }
    const isNonInteractionEvent =
      event.ipexMoment === IpexMomentEnum.nonInteraction;
    if (!this.hasHadFirstInteraction && !isNonInteractionEvent) {
      event.payload.firstInteraction = true;
      this.hasHadFirstInteraction = true;
    }
    this.insightsApi.sendEvent(event);
  }

  getInsightsApi() {
    return this.insightsApi;
  }

  /**
   * Function to set the AB Testing params by name and variation
   *
   * @param {String} abVersionName AB testing name param
   * @param {String} abVersionVariation AB testing variation param
   */
  async setAbVersion(abVersionName, abVersionVariation) {
    if (!(await this.canTransmit())) {
      return;
    }

    this.insightsApi.setAbVersion(abVersionName, abVersionVariation);
  }

  async canTransmit() {
    if (this._isConnected && !(await this.hasConsent())) {
      this.insightsApi.disconnect();
      this._isConnected = false;
    }

    return this._isConnected;
  }
}
